"use client";

import { useRef, useEffect, useContext, useState, ReactNode } from "react";
import { createPath } from "@/service/routing/routing";
import { PictureUpload } from "@/components/common/picture-upload/picture-upload";
import { ResourceContext } from "@/contexts/resource-context";
import { getLogEventData } from "@/service/logs/log";
import { cn } from "@/lib/utils";

export function BannerSlide({
  defaultActive,
  campaignItem: { banner, uuid, bannerType, resourcePatronId, link },
  campaignKey,
  campaignItemKey,
  width,
  height,
}: {
  defaultActive: boolean;
  campaignItem: BannerCampaignItem;
  campaignKey: string;
  campaignItemKey: string;
  width: number;
  height: number;
}) {
  const slideRef = useRef<HTMLDivElement>(null);
  const { logView } = useContext(ResourceContext);
  const [isActive, setIsActive] = useState(defaultActive);
  const [logViewData, setLogViewData] = useState({
    path: "",
    polishName: "",
  });

  useEffect(() => {
    setLogViewData(getLogEventData(logView));

    const node = slideRef.current;
    if (!node) return;

    const observer = new MutationObserver(() => {
      setIsActive(node.classList.contains("banner-slide--active"));
    });

    observer.observe(node, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  function ContentWrapper({ children }: { children: ReactNode }) {
    if (!banner) {
      return <></>;
    }

    return link ? (
      <div style={{ display: "inline" }}>
        <div
          onClick={() => {
            // Podgląd banera (?bannerPreview) nie nabija kliknięć — przekierowanie loguje klik w backendzie.
            if (new URLSearchParams(window.location.search).has("bannerPreview")) {
              return;
            }

            window.open(
              createPath("coop.redirect", {
                uuid,
                logEventData: JSON.stringify(logViewData),
              }),
              "_blank",
              "noopener,noreferrer",
            );
          }}
          className={"absolute-link"}
          style={{ cursor: "pointer" }}
        />
        {children}
      </div>
    ) : (
      <>{children}</>
    );
  }

  return (
    <div
      className={cn(
        "banner-slide",
        {
          // It is to prevent Layout Shift
          "banner-slide--active": defaultActive,
        },
        "hidden max-w-full items-center overflow-hidden outline-1 outline-zinc-100 [&.banner-slide--active]:flex",
      )}
      ref={slideRef}
      data-campaign-index={campaignKey}
      data-banner-index={campaignItemKey}
      data-cid={resourcePatronId}
      data-uuid={uuid}
      data-log-view-data={JSON.stringify(logViewData)}
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      <div className={cn("coop", "relative h-full max-h-full w-full")}>
        {isActive && banner && (
          <ContentWrapper>
            {bannerType === "image" ? (
              <PictureUpload
                image={{ ...(banner as ImageVM), alt: "banner" }}
                loaded={true}
                pictureClassName={
                  "h-full w-full  [&_img]:w-full [&_img]:h-full"
                }
                wrapperClassName={
                  "flex items-center mx-auto  justify-center h-full w-full"
                }
                renderWidth={width}
              />
            ) : (
              <iframe
                className={"h-full w-full"}
                src={banner.url}
                title={banner.url}
              />
            )}
          </ContentWrapper>
        )}
      </div>
    </div>
  );
}
